/**
 * Componente ChartsSection
 * 
 * Seção de gráficos do dashboard.
 * Agrupa todos os gráficos em uma seção organizada e responsiva.
 * 
 * @module components/Dashboard/ChartsSection/ChartsSection
 */

import React, { lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import LoadingIndicator from '../../Common/LoadingIndicator';

// Lazy loading dos componentes de gráficos para code splitting
const TaskDistributionChart = lazy(() => import('./TaskDistributionChart'));
const EquipmentStatusChart = lazy(() => import('./EquipmentStatusChart'));
const WorkloadChart = lazy(() => import('./WorkloadChart'));

/**
 * Componente de Seção de Gráficos
 * 
 * Renderiza uma seção com todos os gráficos do dashboard:
 * - Distribuição de Tarefas (Pizza)
 * - Status dos Equipamentos (Pizza)
 * - Carga de Trabalho (Barras)
 * 
 * @param {object} props - Propriedades do componente
 * @param {Array} props.taskDistribution - Dados de distribuição de tarefas
 * @param {boolean} props.isLoadingTaskDistribution - Loading de distribuição de tarefas
 * @param {Error|string|null} props.errorTaskDistribution - Erro de distribuição de tarefas
 * @param {Array} props.equipmentStatus - Dados de status dos equipamentos
 * @param {boolean} props.isLoadingEquipmentStatus - Loading de status dos equipamentos
 * @param {Error|string|null} props.errorEquipmentStatus - Erro de status dos equipamentos
 * @param {Array} props.workload - Dados de carga de trabalho
 * @param {boolean} props.isLoadingWorkload - Loading de carga de trabalho
 * @param {Error|string|null} props.errorWorkload - Erro de carga de trabalho
 * @param {string} [props.taskPeriodo] - Período para distribuição de tarefas
 * @param {string} [props.workloadPeriodo] - Período para carga de trabalho
 * @param {string} [props.className] - Classes CSS adicionais
 * 
 * @returns {JSX.Element} Componente de seção de gráficos
 * 
 * @example
 * ```jsx
 * <ChartsSection
 *     taskDistribution={taskDistribution}
 *     isLoadingTaskDistribution={isLoading}
 *     errorTaskDistribution={error}
 *     equipmentStatus={equipmentStatus}
 *     isLoadingEquipmentStatus={isLoading}
 *     errorEquipmentStatus={error}
 *     workload={workload}
 *     isLoadingWorkload={isLoading}
 *     errorWorkload={error}
 * />
 * ```
 */
const ChartsSection = ({
    taskDistribution = null,
    isLoadingTaskDistribution,
    errorTaskDistribution = null,
    equipmentStatus = null,
    isLoadingEquipmentStatus,
    errorEquipmentStatus = null,
    workload = null,
    isLoadingWorkload,
    errorWorkload = null,
    taskPeriodo = '30 Dias',
    workloadPeriodo = 'Próximos 7 dias',
    className = '',
}) => {
    // Memoizar props para evitar re-renderizações desnecessárias
    const taskDistributionProps = React.useMemo(() => ({
        data: taskDistribution,
        isLoading: isLoadingTaskDistribution,
        error: errorTaskDistribution,
        periodo: taskPeriodo,
        className: 'md:col-span-1 lg:col-span-1',
    }), [taskDistribution, isLoadingTaskDistribution, errorTaskDistribution, taskPeriodo]);

    const equipmentStatusProps = React.useMemo(() => ({
        data: equipmentStatus,
        isLoading: isLoadingEquipmentStatus,
        error: errorEquipmentStatus,
        className: 'md:col-span-1 lg:col-span-1',
    }), [equipmentStatus, isLoadingEquipmentStatus, errorEquipmentStatus]);

    const workloadProps = React.useMemo(() => ({
        data: workload,
        isLoading: isLoadingWorkload,
        error: errorWorkload,
        periodo: workloadPeriodo,
        className: 'md:col-span-2 lg:col-span-2',
    }), [workload, isLoadingWorkload, errorWorkload, workloadPeriodo]);

    return (
        <section 
            id="charts-section"
            className={`mt-8 ${className}`} 
            data-testid="charts-section"
            aria-labelledby="charts-section-title"
            aria-label="Seção de Gráficos e Visualizações"
        >
            {/* Título oculto para leitores de tela */}
            <h2 id="charts-section-title" className="sr-only">
                Gráficos e Visualizações
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 pt-2 pb-2" role="list" aria-label="Gráficos do dashboard">
                {/* 1. Card: Distribuição de Tarefas (Gráfico de Rosca/Pizza) */}
                <Suspense fallback={<LoadingIndicator />}>
                    <TaskDistributionChart {...taskDistributionProps} />
                </Suspense>

                {/* 2. Card: Status dos Equipamentos (Gráfico de Rosca/Pizza) */}
                <Suspense fallback={<LoadingIndicator />}>
                    <EquipmentStatusChart {...equipmentStatusProps} />
                </Suspense>

                {/* 3. Card: Carga de Trabalho (Gráfico de Barras) - Ocupa 2/4 */}
                <Suspense fallback={<LoadingIndicator />}>
                    <WorkloadChart {...workloadProps} />
                </Suspense>
            </div>
        </section>
    );
};

ChartsSection.propTypes = {
    /** Dados de distribuição de tarefas */
    taskDistribution: PropTypes.array,
    
    /** Loading de distribuição de tarefas */
    isLoadingTaskDistribution: PropTypes.bool.isRequired,
    
    /** Erro de distribuição de tarefas */
    errorTaskDistribution: PropTypes.oneOfType([
        PropTypes.instanceOf(Error),
        PropTypes.string,
        PropTypes.oneOf([null]),
    ]),
    
    /** Dados de status dos equipamentos */
    equipmentStatus: PropTypes.array,
    
    /** Loading de status dos equipamentos */
    isLoadingEquipmentStatus: PropTypes.bool.isRequired,
    
    /** Erro de status dos equipamentos */
    errorEquipmentStatus: PropTypes.oneOfType([
        PropTypes.instanceOf(Error),
        PropTypes.string,
        PropTypes.oneOf([null]),
    ]),
    
    /** Dados de carga de trabalho */
    workload: PropTypes.array,
    
    /** Loading de carga de trabalho */
    isLoadingWorkload: PropTypes.bool.isRequired,
    
    /** Erro de carga de trabalho */
    errorWorkload: PropTypes.oneOfType([
        PropTypes.instanceOf(Error),
        PropTypes.string,
        PropTypes.oneOf([null]),
    ]),
    
    /** Período para distribuição de tarefas */
    taskPeriodo: PropTypes.string,
    
    /** Período para carga de trabalho */
    workloadPeriodo: PropTypes.string,
    
    /** Classes CSS adicionais */
    className: PropTypes.string,
};

// Default props removidos - usando default parameters na função

// Memoizar componente para evitar re-renderizações desnecessárias
export default React.memo(ChartsSection, (prevProps, nextProps) => {
    // Comparação customizada para evitar re-renderizações desnecessárias
    return (
        prevProps.taskDistribution === nextProps.taskDistribution &&
        prevProps.isLoadingTaskDistribution === nextProps.isLoadingTaskDistribution &&
        prevProps.errorTaskDistribution === nextProps.errorTaskDistribution &&
        prevProps.equipmentStatus === nextProps.equipmentStatus &&
        prevProps.isLoadingEquipmentStatus === nextProps.isLoadingEquipmentStatus &&
        prevProps.errorEquipmentStatus === nextProps.errorEquipmentStatus &&
        prevProps.workload === nextProps.workload &&
        prevProps.isLoadingWorkload === nextProps.isLoadingWorkload &&
        prevProps.errorWorkload === nextProps.errorWorkload &&
        prevProps.taskPeriodo === nextProps.taskPeriodo &&
        prevProps.workloadPeriodo === nextProps.workloadPeriodo &&
        prevProps.className === nextProps.className
    );
});

